import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Plus, Ship, Search, Package, ArrowRight, Filter, Megaphone, ListChecks, Handshake, ShieldCheck, Scale
} from "lucide-react";
import { toast } from "sonner";
import AdvertiseModal from "@/pages/trade/AdvertiseModal";
import ShippingProcessModal from "@/pages/trade/ShippingProcessModal";
import PartnerGuideModal from "@/pages/trade/PartnerGuideModal";
import BusinessProtectionModal from "@/pages/trade/BusinessProtectionModal";
import EthiopianLawModal from "@/pages/trade/EthiopianLawModal";

const STATUSES = [
  { value: "ordered", label: "Ordered" },
  { value: "in_transit_china", label: "In Transit · China" },
  { value: "in_transit_djibouti", label: "In Transit · Djibouti" },
  { value: "cleared_djibouti", label: "Cleared Djibouti" },
  { value: "in_transit_ethiopia", label: "In Transit · Ethiopia" },
  { value: "delivered", label: "Delivered" },
];

const ShipmentsList = () => {
  const { API, authHeader } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [advertiseShipment, setAdvertiseShipment] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [partnerGuideOpen, setPartnerGuideOpen] = useState(false);
  const [businessGuideOpen, setBusinessGuideOpen] = useState(false);
  const [lawGuideOpen, setLawGuideOpen] = useState(false);
  const [processShipment, setProcessShipment] = useState(null);

  const fetchShipments = () => {
    axios.get(`${API}/trade/shipments`, { headers: authHeader() })
      .then(res => setShipments(res.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchShipments(); }, []);

  const updateStatus = async (id, newStatus, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await axios.patch(
        `${API}/trade/shipments/${id}/status`,
        { status: newStatus },
        { headers: authHeader() }
      );
      setShipments(prev => prev.map(s => s.id === id ? { ...s, status: newStatus } : s));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update");
    }
  };

  const statuses = [
    { value: "all", label: "All" },
    ...STATUSES,
  ];

  const filtered = shipments.filter(s => {
    const matchSearch = !search || 
      s.shipment_name.toLowerCase().includes(search.toLowerCase()) ||
      (s.supplier_name && s.supplier_name.toLowerCase().includes(search.toLowerCase())) ||
      (s.tracking_number && s.tracking_number.includes(search));
    const matchStatus = statusFilter === "all" || s.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10">
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-start justify-between flex-wrap gap-4 mb-10"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Shipments</p>
            <h1 className="text-4xl md:text-5xl font-serif font-bold">Track Every Container</h1>
            <p className="text-white/50 mt-2">Manage shipments from China to Ethiopia</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => setGuideOpen(true)}
              className="bg-white/10 text-white hover:bg-white/20 px-6 py-6 rounded-sm"
              data-testid="process-guide-btn"
            >
              <ListChecks size={18} className="mr-2" />
              Process Guide
            </Button>
            <Button
              onClick={() => setPartnerGuideOpen(true)}
              className="bg-white/10 text-white hover:bg-white/20 px-6 py-6 rounded-sm"
              data-testid="partner-guide-btn"
            >
              <Handshake size={18} className="mr-2" />
              Partner Guide
            </Button>
            <Button
              onClick={() => setBusinessGuideOpen(true)}
              className="bg-white/10 text-white hover:bg-white/20 px-6 py-6 rounded-sm"
              data-testid="business-protection-btn"
            >
              <ShieldCheck size={18} className="mr-2" />
              Business Protection
            </Button>
            <Button
              onClick={() => setLawGuideOpen(true)}
              className="bg-white/10 text-white hover:bg-white/20 px-6 py-6 rounded-sm"
              data-testid="law-guide-btn"
            >
              <Scale size={18} className="mr-2" />
              Ethiopian Law
            </Button>
            <Link to="/trade/shipments/new">
              <Button className="trade-btn-primary px-6 py-6 rounded-sm" data-testid="new-shipment-btn">
                <Plus size={18} className="mr-2" />
                New Shipment
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="trade-card rounded-sm p-5 mb-8 flex flex-col md:flex-row gap-4"
        >
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search shipments, suppliers, tracking..."
              className="trade-input h-12 pl-11 rounded-sm"
              data-testid="search-shipments-input"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto">
            <Filter className="w-4 h-4 text-white/40 flex-shrink-0" />
            {statuses.map(s => (
              <button
                key={s.value}
                onClick={() => setStatusFilter(s.value)}
                className={`px-3 py-2 text-xs rounded-sm whitespace-nowrap transition-all ${
                  statusFilter === s.value 
                    ? "bg-[#D4AF37] text-black font-semibold"
                    : "bg-white/5 text-white/60 hover:text-white"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Shipments Grid */}
        {loading ? (
          <div className="text-center p-20 text-white/40">Loading shipments...</div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="trade-card rounded-sm p-16 text-center"
          >
            <Ship className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">No shipments found</h3>
            <p className="text-white/50 mb-6">
              {shipments.length === 0 ? "Create your first shipment to get started" : "Try different filters"}
            </p>
            {shipments.length === 0 && (
              <Link to="/trade/shipments/new">
                <Button className="trade-btn-primary rounded-sm px-6">
                  <Plus size={16} className="mr-2" /> Add Shipment
                </Button>
              </Link>
            )}
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <AnimatePresence>
              {filtered.map((s, i) => (
                <motion.div
                  key={s.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <div className="group block trade-card rounded-sm p-6" data-testid={`shipment-card-${s.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <Link to={`/trade/shipments/${s.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                          <Package className="w-6 h-6 trade-gold" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-serif font-semibold text-lg truncate">{s.shipment_name}</h3>
                          <p className="text-xs text-white/50 truncate">
                            {s.supplier_name || "No supplier"}
                          </p>
                        </div>
                      </Link>
                      <div className="relative" onClick={(e) => e.preventDefault()}>
                        <select
                          value={s.status}
                          onChange={(e) => updateStatus(s.id, e.target.value, e)}
                          onClick={(e) => e.stopPropagation()}
                          className={`status-${s.status} px-3 py-1 pr-7 rounded-sm text-xs uppercase tracking-wide font-semibold cursor-pointer border-0 appearance-none`}
                          data-testid={`status-select-${s.id}`}
                          style={{ backgroundImage: "linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%)", backgroundPosition: "calc(100% - 11px) center, calc(100% - 7px) center", backgroundSize: "4px 4px, 4px 4px", backgroundRepeat: "no-repeat" }}
                        >
                          {STATUSES.map(st => (
                            <option key={st.value} value={st.value} style={{ background: "#142039", color: "#fff" }}>
                              {st.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <Link to={`/trade/shipments/${s.id}`} className="block">
                      <div className="flex items-center gap-2 text-xs text-white/50 mb-4">
                        <span>🇨🇳 {s.origin}</span>
                        <ArrowRight size={10} />
                        <span className="trade-green">🇪🇹 {s.destination}</span>
                      </div>

                      {(s.bill_of_lading || s.container_number) && (
                        <div className="mb-3 pb-3 border-b border-white/5 space-y-1">
                          {s.bill_of_lading && (
                            <p className="text-[11px] text-white/60 font-mono flex items-center gap-2">
                              <span className="text-white/40">B/L:</span>
                              <span className="trade-gold">{s.bill_of_lading}</span>
                            </p>
                          )}
                          {s.container_number && (
                            <p className="text-[11px] text-white/60 font-mono flex items-center gap-2">
                              <span className="text-white/40">CTR:</span>
                              <span className="trade-gold">{s.container_number}</span>
                            </p>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/5">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-white/40">Cost (USD)</p>
                          <p className="font-semibold mt-1">${(s.product_cost_usd || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-white/40">Ship (USD)</p>
                          <p className="font-semibold mt-1">${(s.shipping_cost_usd || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-white/40">Revenue (ETB)</p>
                          <p className="font-semibold mt-1 trade-green">{(s.actual_revenue_etb || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    </Link>

                    <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <button
                        onClick={() => setProcessShipment(s)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                        data-testid={`process-checklist-btn-${s.id}`}
                      >
                        <ListChecks size={12} /> Process Checklist
                      </button>
                      <button
                        onClick={() => setAdvertiseShipment(s)}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                        data-testid={`advertise-btn-${s.id}`}
                      >
                        <Megaphone size={12} /> Advertise
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {guideOpen && (
          <ShippingProcessModal onClose={() => setGuideOpen(false)} />
        )}

        {partnerGuideOpen && (
          <PartnerGuideModal onClose={() => setPartnerGuideOpen(false)} />
        )}

        {businessGuideOpen && (
          <BusinessProtectionModal onClose={() => setBusinessGuideOpen(false)} />
        )}

        {lawGuideOpen && (
          <EthiopianLawModal onClose={() => setLawGuideOpen(false)} />
        )}

        {processShipment && (
          <ShippingProcessModal
            shipment={processShipment}
            onClose={() => setProcessShipment(null)}
            onProgress={(shipmentId, checklist) => {
              setShipments(prev => prev.map(s => s.id === shipmentId ? { ...s, process_checklist: checklist } : s));
              setProcessShipment(prev => prev && prev.id === shipmentId ? { ...prev, process_checklist: checklist } : prev);
            }}
          />
        )}

        {advertiseShipment && (
          <AdvertiseModal
            shipment={advertiseShipment}
            onClose={() => setAdvertiseShipment(null)}
          />
        )}

      </div>
    </div>
  );
};

export default ShipmentsList;
